// Read-only render of a workflow's SHAPE for the Template Store detail page.
// Replaces the old canvas screenshot: instead of a flat PNG we draw the real
// node layout + connections from a STRIPPED graph the server derives
// (templateService.buildPreviewGraph) — positions, titles, icons, and edges
// only, never code/params/values.
//
// To MATCH the live editor exactly we reuse the canvas's own tile components and
// CSS (NodeTile + .wf-node-tile* / .wf-node-start-pill from WorkflowNode.css)
// rather than re-styling boxes. Tiles render at their fixed canvas sizes
// (132×132, compact 92×92) at the stored positions, so the preview reads like
// the canvas. It deliberately follows the viewer's theme — same as the canvas.
//
// Deliberately NOT the 4,000-line interactive WorkflowCanvas: this is a small,
// non-interactive renderer (no drag/select/run state). Edges leave/enter via the
// stored anchors (default bottom→top) using the shared bezier helper. Router /
// classifier render as compact tiles too (their wide card needs params we strip).

const React = require('react');
const { createElement: h, useRef, useState, useMemo, useLayoutEffect } = React;
const NodeIcon = require('./NodeIcon');
const NodeTile = require('./NodeTile');
const { anchorDir } = require('../lib/connectionPaths');
const { renderStandbyFlowDots, renderStandbyFlowGradient } = require('../lib/standbyFlowDot');
require('./WorkflowNode.css'); // canvas tile styles (also loaded globally; explicit here)
require('./WorkflowPreviewGraph.css');

// Breathing room around the graph so tiles don't sit flush against the frame.
var PAD = 40;
// Cap the rendered height so a tall (vertical) workflow can't produce a giant
// preview block — scale down further by height when needed.
var MAX_PREVIEW_H = 600;
// Anchors we know how to place; anything else falls back to a sensible default.
var CARDINAL = { top: true, bottom: true, left: true, right: true };

// Fixed canvas tile footprints (see WorkflowNode.css). Set Variable + Condition
// render as the smaller compact tile; Start is a pill (width grows with label).
function nodeSize(n) {
  var icon = n.icon;
  if (icon === 'set-variable' || icon === 'condition') return { w: 92, h: 92 };
  if (icon === 'start') return { w: 150, h: 64 };
  return { w: 132, h: 132 };
}

function rectOf(n) {
  var s = nodeSize(n);
  return {
    left: n.left, top: n.top,
    right: n.left + s.w, bottom: n.top + s.h,
    cx: n.left + s.w / 2, cy: n.top + s.h / 2
  };
}

function bboxOf(nodes) {
  var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  nodes.forEach(function(n) {
    var r = rectOf(n);
    if (r.left < minX) minX = r.left;
    if (r.top < minY) minY = r.top;
    if (r.right > maxX) maxX = r.right;
    if (r.bottom > maxY) maxY = r.bottom;
  });
  if (!isFinite(minX)) return { left: 0, top: 0, width: 1, height: 1 };
  return {
    left: minX - PAD,
    top: minY - PAD,
    width: (maxX - minX) + PAD * 2,
    height: (maxY - minY) + PAD * 2
  };
}

function anchorPt(rect, anchor) {
  if (anchor === 'top') return { x: rect.cx, y: rect.top };
  if (anchor === 'bottom') return { x: rect.cx, y: rect.bottom };
  if (anchor === 'left') return { x: rect.left, y: rect.cy };
  if (anchor === 'right') return { x: rect.right, y: rect.cy };
  return { x: rect.cx, y: rect.cy };
}

function normAnchor(anchor, fallback) {
  return (anchor && CARDINAL[anchor]) ? anchor : fallback;
}

// Bezier between two anchored points — a local variant of
// lib/connectionPaths.smoothPath WITHOUT its hard 60px minimum stem. That floor
// makes the two control points overshoot/cross when the anchor-to-anchor gap is
// short (< ~130px), so the curve loops back on itself — which makes the standby
// flow dot visibly travel BACKWARD along the loop. Clamping the stem to 45% of
// the distance (always under half) keeps the curve monotonic, so the dot only
// ever moves source→target. We always pass both anchors, so that's all we need.
function edgePath(from, to, fromAnchor, toAnchor) {
  var dx = to.x - from.x;
  var dy = to.y - from.y;
  var stem = Math.min(150, Math.sqrt(dx * dx + dy * dy) * 0.45);
  var fd = anchorDir(fromAnchor);
  var td = anchorDir(toAnchor);
  var c1x = from.x + fd.x * stem;
  var c1y = from.y + fd.y * stem;
  var c2x = to.x + td.x * stem;
  var c2y = to.y + td.y * stem;
  return 'M ' + from.x + ' ' + from.y + ' C ' + c1x + ' ' + c1y + ' ' + c2x + ' ' + c2y + ' ' + to.x + ' ' + to.y;
}

// One node positioned at its canvas coordinates, rendered with the real tile
// (or the Start pill) so it matches the editor.
function renderNode(n, bbox) {
  var isStart = n.icon === 'start';
  var isCompact = n.icon === 'set-variable' || n.icon === 'condition';
  var inner;
  if (isStart) {
    inner = h('div', { className: 'wf-node wf-node-start-pill' },
      h('div', { className: 'wf-node-start-icon', style: { background: '#aee7cc', color: '#142320' } },
        h(NodeIcon, { type: 'start', size: 22 })
      ),
      h('div', { className: 'wf-node-start-label' }, n.title || 'Start')
    );
  } else {
    inner = h('div', {
      className: 'wf-node wf-node-tile-wrap wf-node-readonly' + (isCompact ? ' wf-node-tile-wrap-compact' : '')
    },
      h(NodeTile, {
        icon: n.icon,
        logoUrl: n.logoUrl,
        title: n.title,
        subtitle: n.subtitle,
        compact: isCompact
      })
    );
  }
  return h('div', {
    key: n.id,
    className: 'wpg-node',
    style: { left: (n.left - bbox.left) + 'px', top: (n.top - bbox.top) + 'px' }
  }, inner);
}

function WorkflowPreviewGraph(props) {
  var graph = props.graph || {};
  var nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  var connections = Array.isArray(graph.connections) ? graph.connections : [];

  var bbox = useMemo(function() { return bboxOf(nodes); }, [nodes]);

  // Fit the (fixed-size) stage into the container, scaling DOWN only — by width,
  // and by height so a tall workflow stays a reasonable preview size.
  var containerRef = useRef(null);
  var [scale, setScale] = useState(1);
  useLayoutEffect(function() {
    var el = containerRef.current;
    if (!el) return undefined;
    function fit() {
      var w = el.clientWidth || bbox.width;
      setScale(Math.min(1, w / bbox.width, MAX_PREVIEW_H / bbox.height));
    }
    fit();
    var ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(el);
    return function() { if (ro) ro.disconnect(); };
  }, [bbox.width, bbox.height]);

  if (!nodes.length) return null;

  var nodeById = {};
  nodes.forEach(function(n) { nodeById[n.id] = n; });

  // Edge geometry computed once, shifted so the bbox top-left is the origin.
  // The same pathD feeds both the static line AND the travelling standby dot.
  var edgeDescriptors = connections.map(function(c, i) {
    var fromNode = nodeById[c.from];
    var toNode = nodeById[c.to];
    if (!fromNode || !toNode) return null;
    var fromAnchor = normAnchor(c.fromAnchor, 'bottom');
    var toAnchor = normAnchor(c.toAnchor, 'top');
    var fp = anchorPt(rectOf(fromNode), fromAnchor);
    var tp = anchorPt(rectOf(toNode), toAnchor);
    var from = { x: fp.x - bbox.left, y: fp.y - bbox.top };
    var to = { x: tp.x - bbox.left, y: tp.y - bbox.top };
    return { id: c.from + '>' + c.to + '-' + i, className: '', pathD: edgePath(from, to, fromAnchor, toAnchor) };
  }).filter(Boolean);

  var edgeLines = edgeDescriptors.map(function(e) {
    return h('path', { key: e.id, className: 'wpg-edge', d: e.pathD });
  });

  var cards = nodes.map(function(n) { return renderNode(n, bbox); });

  return h('div', {
    className: 'wpg',
    ref: containerRef,
    // Force the store preview to always render LIGHT, regardless of the viewer's
    // theme. This drives the existing [data-theme="light"] start-pill rules; the
    // tile color families are forced light via WorkflowPreviewGraph.css overrides.
    'data-theme': 'light',
    style: { height: (bbox.height * scale) + 'px' }
  },
    h('div', {
      className: 'wpg-stage',
      style: { width: bbox.width + 'px', height: bbox.height + 'px', transform: 'scale(' + scale + ')' }
    },
      h('svg', { className: 'wpg-edges', width: bbox.width, height: bbox.height },
        h('defs', null, renderStandbyFlowGradient(h)),
        edgeLines,
        // Soft gray dot gliding source→target on each edge — the canvas's idle
        // "flow direction" cue (SMIL, GPU-cheap; auto-hidden for reduced motion).
        renderStandbyFlowDots(h, edgeDescriptors)
      ),
      cards
    )
  );
}

module.exports = WorkflowPreviewGraph;
